
import { Users, Package, FileText, DollarSign, Settings, LogOut } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useState } from "react";
import { CustomerDialog } from "./CustomerDialog";
import { CustomerTable } from "./CustomerTable";
import { useNavigate, useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./ui/use-toast";
import { useSidebar } from "@/components/ui/sidebar";
import { UserIdentity } from "./UserIdentity";

const menuItems = [
  { icon: DollarSign, label: "Fluxo de Caixa", href: "/adm/dashboard" },
  { icon: Users, label: "Clientes", href: "/customers" },
  { icon: Package, label: "Estoque", href: "/adm/stock" },
  { icon: FileText, label: "Relatórios", href: "/adm/reports" },
  { icon: FileText, label: "Orçamentos", href: "/adm/quotations" },
  { icon: Settings, label: "Configurações", href: "/adm/settings" },
];

export function DashboardSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCustomerDialogOpen, setIsCustomerDialogOpen] = useState(false);
  const [showCustomerTable, setShowCustomerTable] = useState(false);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { setOpen } = useSidebar();

  const handleMenuClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    
    // Se o modal de clientes estiver aberto, feche-o primeiro
    if (showCustomerTable) {
      setShowCustomerTable(false);
    }

    if (href === "#") return;
    
    if (href === "/customers") {
      setShowCustomerTable(true);
    } else {
      navigate(href);
    }

    // Fecha o menu lateral no mobile após selecionar um item
    if (isMobile) {
      setOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Fecha o modal de clientes se estiver aberto
      if (showCustomerTable) {
        setShowCustomerTable(false);
      }

      await supabase.auth.signOut();
      navigate("/auth");
      
      // Fecha o menu no mobile após fazer logout
      if (isMobile) {
        setOpen(false);
      }
      
      toast({
        title: "Logout realizado com sucesso",
        description: "Você foi desconectado do sistema",
      });
    } catch (error) {
      toast({
        title: "Erro ao realizar logout",
        description: "Tente novamente mais tarde",
        variant: "destructive",
      });
    }
  };

  const handleCloseCustomerModal = () => {
    setShowCustomerTable(false);
    setIsCustomerDialogOpen(false);
  };

  const isActiveRoute = (href: string) => {
    if (href === "#") return false;
    if (href === "/customers") return showCustomerTable;
    return location.pathname === href;
  };

  return (
    <>
      <Sidebar className={`${isMobile ? "absolute z-50" : ""} bg-[#0073B7]`}>
        <SidebarContent>
          <div className="flex flex-col items-center p-4 space-y-4">
            <img 
              src="/lovable-uploads/df36f051-6586-421c-8e3f-5e4e41162b5f.png" 
              alt="Solucionar Soldas Logo" 
              className="w-32 h-32 object-contain"
            />
            <h1 className="text-xl font-bold text-white font-['Russo_One']">Solucionar Soldas</h1>
          </div>
          <UserIdentity variant="sidebar" />
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {menuItems.map((item) => (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton asChild>
                      <a
                        href={item.href}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          isActiveRoute(item.href) 
                            ? "bg-[#E60026] text-white" 
                            : "text-white hover:bg-[#004080]"
                        }`}
                        onClick={(e) => handleMenuClick(e, item.href)}
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="font-['Roboto']">{item.label}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                {/* Logout Button */}
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-3 py-2 rounded-lg transition-colors text-white hover:bg-[#004080]"
                    >
                      <LogOut className="h-5 w-5" />
                      <span className="font-['Roboto']">Sair</span>
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>

      {showCustomerTable && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 overflow-y-auto">
          <div className="fixed inset-4 md:inset-x-[20%] md:inset-y-[10%] bg-card rounded-lg shadow-lg p-4 md:p-6 overflow-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl md:text-2xl font-bold">Gerenciamento de Clientes</h2>
              <button
                onClick={handleCloseCustomerModal}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => setIsCustomerDialogOpen(true)}
                className="w-full md:w-auto bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90"
              >
                Novo Cliente
              </button>
              <CustomerTable />
            </div>
          </div>
        </div>
      )}

      <CustomerDialog
        isOpen={isCustomerDialogOpen}
        onOpenChange={setIsCustomerDialogOpen}
        onSave={() => {}}
      />
    </>
  );
}
